<template>
    <div :class="['relative flex items-center', className]">
        <Select :modelValue="modelValue" :options="options" optionLabel="label" optionValue="value"
            :placeholder="placeholder" class="w-full text-sm" :class="{ 'p-disabled': isDisabled }" :disabled="isDisabled"
            :size="size"
            filter showClear @update:modelValue="onUpdate" @change="onChange" :loading="loading"
            data-testid="academic-calendar-select" />
        <button v-if="isDisabled" @click="enableSelection"
            class="absolute right-7 text-gray-500 hover:text-primary z-10 p-0.5 bg-white rounded-full shadow-sm border border-gray-200 transition-colors"
            title="Edit Term" type="button" data-testid="btn-enable-calendar-select">
            <i class="pi pi-pencil text-[10px]"></i>
        </button>
    </div>
</template>

<script setup lang="ts">
import { QUERY_SCHOOL } from '@/graphql';
import { useAcademicCalendar } from '@/composables/queries/useAcademicCalendar';

const props = defineProps({
  modelValue: {
    type: [String, Number, Object] as PropType<string | number | object | null>,
    default: null,
  },
  placeholder: {
    type: String,
    default: 'Select Term',
  },
  className: {
    type: String,
    default: 'w-full',
  },
  size: {
    type: String as PropType<'small' | 'large' | undefined>,
    default: 'small',
  },
  // Pre-select the active calendar when nothing is chosen. Views that mean
  // "no selection = everything" (e.g. all-time course registrations) turn
  // this off so the default genuinely stays unfiltered.
  autoSelectActive: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['update:modelValue', 'change']);

const isDisabled = ref(true);

const { result: schoolResult, loading } = QUERY_SCHOOL();
const { activeAcademicCalendar } = useAcademicCalendar({ activeAcademicCalendar: { autoFetch: true } });

import type { AcademicCalendar } from '@/types/composition-functions';

const options = computed(() => schoolResult.value?.getSchool?.academicCalendars?.map((c: AcademicCalendar) => ({
  label: `${c.session?.name || ''} - ${c.termStructure?.name || ''}`,
  value: c.id,
})) || []);

// Set default value to active calendar if modelValue is empty
watch(() => activeAcademicCalendar.academicCalendar.value, (active) => {
  if (props.autoSelectActive && active && !props.modelValue) {
    emit('update:modelValue', active.id);
  }
}, { immediate: true });

// Also check on mount if data is already there
onMounted(() => {
  const active = activeAcademicCalendar.academicCalendar.value;
  if (props.autoSelectActive && active && !props.modelValue) {
    emit('update:modelValue', active.id);
  }
});

const onUpdate = (value: string | number | null) => {
  emit('update:modelValue', value);
};

const onChange = (event: unknown) => {
  emit('change', event);
};

const enableSelection = () => {
  isDisabled.value = false;
};
</script>

<style scoped>
.p-disabled {
    opacity: 0.7;
    pointer-events: none;
}
</style>
